import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogoSvg } from "../svgs/LogoSvg";
import { navigationMenus, PATH_CONTACT } from "../../utils/constants";
import { PrimaryButton } from "../common/PrimaryButton";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

export const Header = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSectionsOpen, setMobileSectionsOpen] = useState<{ services: boolean; about: boolean; infos: boolean }>({ services: false, about: false, infos: false });
  const [openDropdown, setOpenDropdown] = useState<null | "services" | "about" | "infos">(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSectionsOpen({ services: false, about: false, infos: false });
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  // Periodic animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 2000); // Animation duration
    }, 30000); // Repeat every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div className="h-16 sm:h-24" />

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out
          bg-white dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700 
          ${
            scrolled
              ? "bg-gradient-to-b from-white via-white to-ergo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 shadow-sm"
              : "bg-white dark:bg-gray-800"
          }`}
      >
        <nav>
          <div className="flex h-16 sm:h-24 items-center justify-between bg-white dark:bg-gray-800 px-6 sm:px-8 lg:px-12">
            {/* Logo - Left aligned */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-4">
                <span className="sr-only">ERGOTHERAPIE UND ZEIT(T)RÄUME</span>
                <LogoSvg classNames="h-7 sm:h-9 xl:h-11 w-auto fill-current text-ergo-500 dark:text-ergo-400 transition-transform duration-300 hover:scale-105" />
                <div className="flex xl:hidden 2xl:flex flex-col">
                  <span className="text-xs sm:text-sm xl:text-base font-medium tracking-wider text-gray-900 dark:text-white">
                    ERGOTHERAPIE
                  </span>
                  <span className="text-xs sm:text-sm xl:text-base font-medium tracking-wider text-ergo-600 dark:text-ergo-400">
                    UND ZEIT(T)RÄUME
                  </span>
                </div>
              </Link>
            </div>

            {/* Navigation Links - Centered with flex-grow */}
            <div className="flex-grow hidden xl:flex justify-center">
              <nav className="flex items-center gap-6 2xl:gap-10">
                {/* Home link */}
                <Link
                  to={navigationMenus.home.href}
                  className={`relative px-2 py-2 text-sm xl:text-base tracking-wide transition-all duration-300 group ${
                    location.pathname === navigationMenus.home.href
                      ? "text-ergo-600 dark:text-ergo-400 font-medium"
                      : "text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 font-normal"
                  }`}
                >
                  {navigationMenus.home.name}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full transform origin-left transition-transform duration-300 ease-out ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-400 scale-x-100"
                        : "bg-ergo-300 scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>

                {/* Leistungen dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("services")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors">
                    {navigationMenus.services.label}
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <div
                    className={`${openDropdown === "services" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[520px]`}
                  >
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-xl p-4">
                      <div className="grid grid-cols-2 gap-1">
                        {navigationMenus.services.items.map((item, index) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Über uns dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("about")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors">
                    {navigationMenus.about.label}
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <div
                    className={`${openDropdown === "about" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[280px]`}
                  >
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-xl p-4">
                      <div className="grid grid-cols-1 gap-1">
                        {navigationMenus.about.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Infos dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDropdown("infos")}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="inline-flex items-center gap-1 px-2 py-2 text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors">
                    {navigationMenus.infos.label}
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <div
                    className={`${openDropdown === "infos" ? "visible opacity-100" : "invisible opacity-0"} transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[320px]`}
                  >
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-xl p-4">
                      <div className="grid grid-cols-1 gap-1">
                        {navigationMenus.infos.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              location.pathname === item.href
                                ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            {/* Contact Button - Right aligned */}
            <div className="flex-shrink-0 hidden xl:block">
              <PrimaryButton to={PATH_CONTACT} periodic>
                Kontakt
              </PrimaryButton>
            </div>

            {/* Mobile menu button */}
            <div className="flex xl:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-ergo-600 dark:text-ergo-400"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Menü öffnen</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu panel */}
          <div
            className={`fixed inset-0 z-50 xl:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all duration-300 ease-in-out 
              ${mobileMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-full"}`}
            style={{ top: "64px" }}
          >
            <div className="flex flex-col h-full">
              {/* Navigation Links - Mobile grouped sections */}
              <div className="flex-grow overflow-y-auto px-6 pt-8 pb-8">
                <div className="space-y-6">
                  {/* Home */}
                  <Link
                    to={navigationMenus.home.href}
                    className={`block rounded-xl px-4 py-4 text-center transition-colors ${
                      location.pathname === navigationMenus.home.href
                        ? "bg-ergo-50/50 dark:bg-gray-800/50 font-semibold text-ergo-600 dark:text-ergo-400"
                        : "bg-gray-50 dark:bg-gray-800/30 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                    }`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileSectionsOpen({ services: false, about: false, infos: false });
                    }}
                  >
                    {navigationMenus.home.name}
                  </Link>

                  {/* Leistungen */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, services: !s.services }))}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700/60"
                    >
                      <span className="text-base font-medium text-gray-800 dark:text-gray-200">{navigationMenus.services.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.services ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.services ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {navigationMenus.services.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Über uns */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, about: !s.about }))}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700/60"
                    >
                      <span className="text-base font-medium text-gray-800 dark:text-gray-200">{navigationMenus.about.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.about ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.about ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 gap-2">
                          {navigationMenus.about.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Infos */}
                  <div>
                    <button
                      onClick={() => setMobileSectionsOpen((s) => ({ ...s, infos: !s.infos }))}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700/60"
                    >
                      <span className="text-base font-medium text-gray-800 dark:text-gray-200">{navigationMenus.infos.label}</span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${mobileSectionsOpen.infos ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${mobileSectionsOpen.infos ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 gap-2">
                          {navigationMenus.infos.items.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`block px-4 py-3 rounded-lg transition-colors ${
                                location.pathname === item.href
                                  ? "bg-ergo-50/80 text-ergo-700 dark:bg-gray-800/60 dark:text-ergo-300"
                                  : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              }`}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileSectionsOpen({ services: false, about: false, infos: false });
                            }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Button */}
              <div className="p-6">
                <PrimaryButton
                  to={PATH_CONTACT}
                  periodic
                  className="w-full shadow-[0_4px_20px_-2px_rgba(139,211,230,0.3)] dark:shadow-[0_4px_20px_-2px_rgba(139,211,230,0.15)]"
                >
                  <span className="text-lg py-2">Kontakt</span>
                </PrimaryButton>
              </div>
            </div>
          </div>

          {/* Background overlay when mobile menu is open */}
          <div
            className={`fixed inset-0 bg-black/10 dark:bg-black/20 backdrop-blur-sm transition-opacity duration-300 xl:hidden
              ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            style={{ top: "64px" }}
            onClick={() => setMobileMenuOpen(false)}
          />
        </nav>
      </header>
    </>
  );
};
